/**
 * #7500 / PR #7572 review — the ONE closed org position level vocabulary.
 *
 * Governed org states carry a position `level` in one of the conventions
 * below (compact band codes, numbered ladders, titled bands, enum codes).
 * Every reader that needs leadership identity (the VP-impact ranking, level
 * anchors) resolves the value through this table by exact canonical key —
 * never by substring or free-text matching, and never from the display
 * title. A value outside the vocabulary resolves to `null`, so callers fail
 * closed instead of guessing a band.
 */
export const ORG_POSITION_LEVEL_BANDS = [
  'executive',
  'vice_president',
  'director',
  'manager',
  'lead',
  'individual_contributor',
] as const;

export type OrgPositionLevelBand = (typeof ORG_POSITION_LEVEL_BANDS)[number];

type BandEntry = readonly [key: string, band: OrgPositionLevelBand];

/**
 * Closed vocabulary keyed by canonical level key (see
 * `orgPositionLevelKey`). Numbered ladders follow the governed ten-step
 * ladder used by the seeded enterprise org states (L10 chief executive,
 * L9 chief officers, L8 unit heads / senior vice presidents, L7 directors,
 * L6 senior managers, L5 leads).
 */
const ORG_POSITION_LEVEL_BAND_ENTRIES: readonly BandEntry[] = [
  ['exec', 'executive'],
  ['executive', 'executive'],
  ['c suite', 'executive'],
  ['l1 ceo', 'executive'],
  ['l2 executive', 'executive'],
  ['l9', 'executive'],
  ['l10', 'executive'],
  ['vp', 'vice_president'],
  ['svp', 'vice_president'],
  ['vice president', 'vice_president'],
  ['senior vice president', 'vice_president'],
  ['l2 vice president', 'vice_president'],
  ['l8', 'vice_president'],
  ['director', 'director'],
  ['l3 director', 'director'],
  ['l7', 'director'],
  ['manager', 'manager'],
  ['l4 manager', 'manager'],
  ['l6', 'manager'],
  ['lead', 'lead'],
  ['l5 lead supervisor', 'lead'],
  ['l5', 'lead'],
  ['ic', 'individual_contributor'],
  ['ic1', 'individual_contributor'],
  ['ic2', 'individual_contributor'],
  ['senior ic', 'individual_contributor'],
  ['individual contributor', 'individual_contributor'],
  ['l6 individual contributor', 'individual_contributor'],
];

const ORG_POSITION_LEVEL_BAND_BY_KEY: ReadonlyMap<string, OrgPositionLevelBand> =
  new Map(ORG_POSITION_LEVEL_BAND_ENTRIES);

/**
 * Canonical lookup key for a governed level value: lower-cased with every
 * separator run collapsed to one space. This is key canonicalization only —
 * the band decision is the exact-key table lookup below.
 */
export function orgPositionLevelKey(level: unknown): string | null {
  if (typeof level !== 'string') return null;
  const key = level
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return key || null;
}

/** The governed band for a position level value, or null when the value is outside the closed vocabulary. */
export function orgPositionLevelBand(level: unknown): OrgPositionLevelBand | null {
  const key = orgPositionLevelKey(level);
  return key ? (ORG_POSITION_LEVEL_BAND_BY_KEY.get(key) ?? null) : null;
}

/** One typed predicate for vice-president band membership; no caller re-reads the level text. */
export function isOrgPositionLevelVicePresident(level: unknown): boolean {
  return orgPositionLevelBand(level) === 'vice_president';
}
